<template>
    <transition name="fab-fade">
        <div v-if="isVisible" class="ai-fab-root">
            <!-- Panel (open state) -->
            <transition name="panel-slide">
                <!-- v-show keeps the conversation mounted while the panel is collapsed. This lets
                     the user reach covered page actions and still allows a successful save event
                     to reset the existing auth-group conversation. -->
                <div v-show="open" class="ai-panel" role="dialog" aria-label="AI Asistan">
                    <header class="ai-panel-header">
                        <div class="header-left">
                            <span class="header-avatar" aria-hidden="true">✨</span>
                            <div class="header-title">
                                <div class="title-main">
                                    <span class="title-name">Vega AI</span>
                                    <span class="beta-badge">Beta</span>
                                </div>
                                <div class="title-sub" v-if="customerName">
                                    {{ customerName }}
                                </div>
                            </div>
                        </div>
                        <div class="header-actions">
                            <button type="button" class="icon-btn" @click="openHistory"
                                    title="Sohbet geçmişi" aria-label="Sohbet geçmişi">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" stroke-width="2"
                                     stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path d="M3 3v5h5"></path>
                                    <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"></path>
                                    <path d="M12 7v5l3 2"></path>
                                </svg>
                            </button>
                            <button type="button" class="icon-btn" @click="resetPanel"
                                    title="Yeni sohbet" aria-label="Yeni sohbet">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" stroke-width="2"
                                     stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                    <line x1="9" y1="10" x2="15" y2="10"></line>
                                    <line x1="12" y1="7" x2="12" y2="13"></line>
                                </svg>
                            </button>
                            <button type="button" class="icon-btn" @click="open = false"
                                    title="Kapat" aria-label="Kapat">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" stroke-width="2"
                                     stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path d="M18 6 6 18"></path>
                                    <path d="m6 6 12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </header>
                    <div class="ai-panel-body">
                        <assistant-panel
                            ref="panel"
                            v-if="routeContext"
                            :customer-id="routeContext.customerId || ''"
                            :customer-name="customerName"
                            :scope="scope" />
                    </div>
                </div>
            </transition>

            <!-- FAB — sadece panel kapalıyken görünür -->
            <transition name="fab-pop">
                <button
                    v-if="!open"
                    type="button"
                    class="ai-fab"
                    @click="toggle"
                    aria-label="Asistan paneli aç">
                    <span class="fab-icon" aria-hidden="true">✨</span>
                </button>
            </transition>
        </div>
    </transition>
</template>

<script>
    import axios from '../../services/index';
    import AssistantPanel from './AssistantPanel.vue';

    export default {
        name: 'AiAssistantFab',
        components: { AssistantPanel },
        data() {
            return {
                open: false,
                scope: 'customer',
                customerName: '',
                customerNameCache: {}, // { customerId: name }
                hasAccess: false
            };
        },
        computed: {
            // Genişletilebilir route → context map. İleride başka sayfalar buraya eklenir.
            routeContext() {
                const route = this.$route;
                if (!route) return null;

                // Vega'da yetki grubu düzenleme ekranı yoktur; asistan yalnız danışan özetinin
                // sekmelerinde açılır (hepsi 'customerSummary' route'unun altında).
                const cid = route.params && route.params.customerId;
                const inCustomerArea = !!cid && Array.isArray(route.matched)
                    && route.matched.some(r => r.name === 'customerSummary');
                return inCustomerArea ? { feature: 'customer', customerId: cid } : null;
            },
            // Panelin bağlamı yalnız route'tan gelir; kullanıcı panel içinden değiştiremez.
            activeSubjectId() {
                const ctx = this.routeContext;
                return ctx ? ctx.customerId : null;
            },
            isVisible() {
                return this.hasAccess && this.routeContext !== null;
            }
        },
        watch: {
            // "En üste git" butonu bu sınıfa bakıp panel açıkken kendini gizler.
            open(v) {
                document.body.classList.toggle('ai-panel-open', v);
            },
            // Açık danışan değişince panel reset.
            activeSubjectId: {
                immediate: true,
                handler(newId, oldId) {
                    if (!newId || newId === oldId) return;
                    this.customerName = '';
                    this.loadCustomerName(newId);
                    if (this.$refs.panel && this.open) this.$refs.panel.reset();
                }
            },

            routeContext(newVal, oldVal) {
                if (newVal && !oldVal) this.checkAccess();
            },
            // Görünürlük kaybolursa paneli kapat (route değişti)
            isVisible(v) {
                if (!v) this.open = false;
            }
        },
        mounted() {
            this.checkAccess();
            this._onKey = (e) => {
                // ESC ile paneli FAB boyutuna kapat
                if (e.key === 'Escape' && this.open) {
                    e.preventDefault();
                    this.open = false;
                }
            };
            document.addEventListener('keydown', this._onKey);
        },
        beforeUnmount() {
            if (this._onKey) document.removeEventListener('keydown', this._onKey);
            document.body.classList.remove('ai-panel-open');
        },
        methods: {
            async checkAccess() {
                try {
                    const res = await axios.get('/api/AiAssistant/access');
                    this.hasAccess = !!(res && res.data && res.data.canUse);
                } catch {
                    this.hasAccess = false;
                }
            },
            toggle() {
                this.open = !this.open;
            },
            resetPanel() {
                if (this.$refs.panel && typeof this.$refs.panel.reset === 'function') {
                    this.$refs.panel.reset();
                }
            },
            openHistory() {
                if (this.$refs.panel && typeof this.$refs.panel.toggleHistory === 'function') {
                    this.$refs.panel.toggleHistory();
                }
            },
            async loadCustomerName(customerId) {
                if (!customerId) { this.customerName = ''; return; }
                if (this.customerNameCache[customerId]) {
                    this.customerName = this.customerNameCache[customerId];
                    return;
                }
                try {
                    const res = await axios.get('/Customer/GetCustomerInfo', {
                        params: { id: customerId }
                    });
                    const name = res && res.data && res.data.name ? res.data.name : '';
                    this.customerName = name;
                    if (name) this.customerNameCache[customerId] = name;
                } catch {
                    this.customerName = '';
                }
            }
        }
    };
</script>

<style scoped>
    /* ═══════════════════════════════════════════════════════════
       Vega AI — Estesoft.App AiAssistantFab.vue'nin Vega kopyası.
       Brand: indigo (#6366f1) tek vurgu rengi.
       ═══════════════════════════════════════════════════════════ */

    .ai-fab-root {
        /* AssistantPanel ile aynı rainbow paleti */
        --ai-rainbow: linear-gradient(135deg,
            #6366f1 0%, #7c5cf0 28%, #a855f7 52%, #ec4899 78%, #6366f1 100%);
        position: fixed;
        bottom: 24px;
        right: 24px;
        z-index: 9998;
    }

    /* ─── FAB ─── */
    /* position: relative + bottom → "en üste git" butonu görününce FAB onun üstüne
       kayar. Kaydırma yalnız butonu taşır, root'un kutusunu değiştirmediği için
       panelin konumu ve açılış animasyonunun çıkış noktası aynı kalır. */
    .ai-fab {
        position: relative;
        /* Sayfanın altına yapışık bir aksiyon çubuğu varsa (danışan formu) FAB
           onun üstüne çıkar; çubuk yoksa değişken tanımsızdır ve kayma 0'dır.
           Değeri çubuğun kendi katmanı verir (customer-form.css). */
        bottom: var(--est-overlay-lift, 0px);
        width: 56px;
        height: 56px;
        border-radius: 50%;
        border: none;
        cursor: pointer;
        background: var(--ai-rainbow);
        background-size: 200% 200%;
        animation: ai-shimmer 7s ease infinite;
        color: white;
        box-shadow: 0 10px 24px rgba(124, 92, 240, 0.34),
                    0 2px 6px rgba(15, 23, 42, 0.08);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1),
                    box-shadow 0.2s ease,
                    bottom 0.2s ease;
    }
    /* 20px kenar + 36px buton + 12px boşluk - 24px root kenarı = 44px. */
    body.has-scrolltop .ai-fab {
        bottom: calc(44px + var(--est-overlay-lift, 0px));
    }
    .ai-fab:hover {
        transform: translateY(-2px);
        box-shadow: 0 14px 32px rgba(168, 85, 247, 0.44),
                    0 3px 8px rgba(15, 23, 42, 0.10);
    }
    @keyframes ai-shimmer {
        0%   { background-position: 0% 50%; }
        50%  { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
    }
    .ai-fab:active { transform: translateY(0); }
    .fab-icon {
        font-size: 22px;
        line-height: 1;
    }

    /* ─── Panel ─── */
    /* Panel BR köşesi FAB BR ile aynı hizada (ai-fab-root içinde bottom:0 right:0).
       Bu sayede transform-origin: bottom right + scale(0) → FAB konumundan büyür. */
    .ai-panel {
        position: absolute;
        bottom: 0;
        right: 0;
        /* İlk açılışta karşılama + dört hızlı soru kaydırmadan sığsın diye clientapp'ten geniştir. */
        width: 440px;
        max-width: calc(100vw - 48px);
        height: 640px;
        max-height: calc(100vh - 48px);
        background: #ffffff;
        border-radius: 16px;
        box-shadow: 0 20px 50px rgba(15, 23, 42, 0.14),
                    0 4px 12px rgba(15, 23, 42, 0.06);
        border: 1px solid #e2e8f0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    /* Header */
    .ai-panel-header {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        background: #ffffff;
        border-bottom: 1px solid #f1f5f9;
        flex-shrink: 0;
    }
    /* İnce rainbow aksan çizgisi — panelin AI kimliğini taçlandırır */
    .ai-panel-header::after {
        content: '';
        position: absolute;
        left: 0; right: 0; bottom: -1px;
        height: 2px;
        background: var(--ai-rainbow);
        background-size: 200% 200%;
        animation: ai-shimmer 7s ease infinite;
        opacity: 0.85;
    }
    .header-left {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }
    .header-avatar {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        background: var(--ai-rainbow);
        background-size: 200% 200%;
        animation: ai-shimmer 7s ease infinite;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 15px;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(124, 92, 240, 0.30);
    }
    .header-title { min-width: 0; line-height: 1.3; }
    .title-main {
        display: flex;
        align-items: center;
        gap: 6px;
    }
    .title-name {
        font-size: var(--est-fs-title);
        font-weight: 700;
        letter-spacing: -0.01em;
        background: var(--ai-rainbow);
        background-size: 200% 200%;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
        animation: ai-shimmer 7s ease infinite;
    }
    .beta-badge {
        font-size: var(--est-fs-meta);
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #7c5cf0;
        background: #f3f0ff;
        border: 1px solid #e4dcff;
        border-radius: 6px;
        padding: 1px 5px;
        line-height: 1.5;
    }
    .title-sub {
        font-size: var(--est-fs-meta);
        color: #64748b;
        margin-top: 1px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 240px;
    }
    .header-actions { display: flex; gap: 4px; }
    .icon-btn {
        width: 36px;
        height: 36px;
        border: none;
        background: transparent;
        color: #64748b;
        border-radius: 10px;
        cursor: pointer;
        font-size: 19px;
        line-height: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease, color 0.15s ease;
    }
    .icon-btn:hover {
        background: #f1f5f9;
        color: #0f172a;
    }

    /* Body */
    .ai-panel-body {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        background: #ffffff;
    }

    /* ─── Transitions ─── */
    /* Panel açılırken FAB'in TAM yerinden büyür, kapanırken oraya küçülür.
       Hesap: panel bottom: 76px → FAB'in BR köşesine inmek için 76px aşağı çekilir,
       scale(0) ile sıfırdan başlar → büyüdükçe FAB konumundan ortaya çıkar. */
    .panel-slide-enter-active {
        transition: opacity 0.14s ease-out 0.04s,
                    transform 0.26s cubic-bezier(0.22, 1.05, 0.32, 1.05);
        transform-origin: 100% 100%; /* sağ-alt köşe = FAB tarafı */
    }
    .panel-slide-leave-active {
        transition: opacity 0.18s ease-in,
                    transform 0.20s cubic-bezier(0.55, 0, 0.85, 0.40);
        transform-origin: 100% 100%;
    }
    .panel-slide-enter-from, .panel-slide-leave-to {
        opacity: 0;
        /* Panel BR köşesi FAB BR köşesiyle aynı hizada (bottom:0 right:0).
           scale(0) + origin bottom-right → FAB'ın en alt köşesinden büyür. */
        transform: scale(0);
    }

    /* FAB görünürlük (route-based fade) */
    .fab-fade-enter-active, .fab-fade-leave-active {
        transition: opacity 0.18s ease, transform 0.18s ease;
    }
    .fab-fade-enter-from, .fab-fade-leave-to {
        opacity: 0;
        transform: translateY(6px);
    }

    /* FAB pop — panel kapanırken yerinde belirir,
       panel açılırken yerinde kaybolur (panel ile yer değiştirir hissi). */
    .fab-pop-enter-active {
        transition: opacity 0.16s ease 0.10s,
                    transform 0.22s cubic-bezier(0.34, 1.6, 0.5, 1) 0.08s;
    }
    .fab-pop-leave-active {
        transition: opacity 0.10s ease, transform 0.14s ease;
    }
    .fab-pop-enter-from, .fab-pop-leave-to {
        opacity: 0;
        transform: scale(0.3);
    }

    /* ─── Mobile ─── */
    @media (max-width: 768px) {
        .ai-fab-root { bottom: 16px; right: 16px; }
        .ai-fab { width: 52px; height: 52px; }
        .fab-icon { font-size: 20px; }
        /* 16px kenar + 36px buton + 12px boşluk - 16px root kenarı = 48px. */
        body.has-scrolltop .ai-fab { bottom: calc(48px + var(--est-overlay-lift, 0px)); }
        .ai-panel {
            position: fixed;
            bottom: 0; right: 0; left: 0; top: 0;
            width: 100vw; height: 100vh; max-height: 100vh;
            border-radius: 0;
            border: none;
        }
        .title-sub { max-width: 180px; }
    }
</style>
